/* eslint-disable react/no-array-index-key */
import { IFormFileDrawingItem } from '@file/projectFile/projectFile.models';
import { Accordion, ComponentType, LoadingButton } from '@gcl/react-core';
import { ReactElement, useEffect, useState } from 'react';
import {
  ITransmittalItemTableFormMobileRow,
  TransmittalItemTableFormMobileRow,
} from './TransmittalItemTableFormMobileRow';
import { TransmittalItemTableFormMobileDetailModal } from './TransmittalItemTableFormMobileDetailModal';
import {
  getTransmittalGeneralDetail,
  useCheckerTransmittalRevisionsForApproval,
} from '../transmittalItemTableForm.hooks';

interface ITransmittalItemTableFormMobile
  extends Omit<
    ITransmittalItemTableFormMobileRow,
    'item' | 'rowIndex' | 'setSelectedRow' | 'fileToUpload' | 'isShowDetailModal' | 'cellExtras'
  > {
  items: IFormFileDrawingItem[];
  isAllowUploadDocument: boolean;
  headerButtons: ReactElement[];
  hasDeletePermission: boolean;
  filesToUpload: {
    [key: string]: File;
  };
  isRefetchingTransmittalItems: boolean;
  isWindowFocus: boolean;
  isAnyActionLoading: boolean;
}

export const TransmittalItemTableFormMobile = ({
  items,
  isAllowUploadDocument,
  headerButtons,
  hasDeletePermission,
  filesToUpload,
  isRefetchingTransmittalItems,
  isWindowFocus,
  isAnyActionLoading,
  ...rest
}: ITransmittalItemTableFormMobile) => {
  const {
    useFormMethods,
    transmittalId,
    tableIndex,
    queryKey,
    docType,
    setIsBlockRefetch,
    setFilesToUpload,
    setRowUploading,
    isReadOnly,
  } = rest;
  const { transmittalType, masterScheduleId } = getTransmittalGeneralDetail(transmittalId);
  const [selectedRow, setSelectedRow] = useState(-1);
  const [isShowDetailModal, setIsShowDetailModal] = useState(false);

  useEffect(() => {
    setIsShowDetailModal(selectedRow >= 0);
  }, [selectedRow]);

  const {
    isSelectAllTransmittalRevisionsForApproval,
    mutationSelectAllTransmittalRevisionsForApproval,
    onClickSelectAll: onSelectAllTransmittalRevisionsForApproval,
  } = useCheckerTransmittalRevisionsForApproval(masterScheduleId, transmittalId, items, docType);
  const {
    isLoading: isLoadingSelectAllTransmittalRevisionsForApproval,
    isSuccess: isSuccessSelectAllTransmittalRevisionsForApproval,
    isError: isErrorSelectAllTransmittalRevisionsForApproval,
  } = mutationSelectAllTransmittalRevisionsForApproval;

  return (
    <>
      <Accordion
        className="gcl-gems-transmittal-item-table-form-mobile"
        content={{
          id: `${docType.name}`,
          onRenderBody: () => {
            return (
              <>
                {items.map((item, index) => {
                  return (
                    <TransmittalItemTableFormMobileRow
                      cellExtras={{
                        isWindowFocus,
                        isRefetchingTransmittalItems,
                        mutationSelectAllTransmittalRevisionsForApproval,
                      }}
                      isShowDetailModal={isShowDetailModal}
                      key={`transmittal-item-table-form-mobile-${index}`}
                      item={item}
                      rowIndex={index}
                      setSelectedRow={setSelectedRow}
                      fileToUpload={item.form?.uuid ? filesToUpload[item.form?.uuid] : undefined}
                      {...rest}
                    />
                  );
                })}
              </>
            );
          },
          onRenderHeader: () => {
            return <span>{docType.name}</span>;
          },
          onRenderHeaderButtons: () => {
            if (isSelectAllTransmittalRevisionsForApproval.isShow)
              return [
                ...headerButtons,
                <LoadingButton
                  key={`ct-checkbox-check-all-${docType.id}`}
                  timeToResetSuccessState={3000}
                  timeToResetErrorState={3000}
                  id={`ct-checkbox-check-all-${docType.id}`}
                  disabled={
                    isAnyActionLoading ||
                    isRefetchingTransmittalItems ||
                    isLoadingSelectAllTransmittalRevisionsForApproval
                  }
                  small
                  type={ComponentType.Primary}
                  onClick={() => {
                    onSelectAllTransmittalRevisionsForApproval(isSelectAllTransmittalRevisionsForApproval.isSelectAll);
                  }}
                  isSuccess={isSuccessSelectAllTransmittalRevisionsForApproval}
                  isError={isErrorSelectAllTransmittalRevisionsForApproval}
                  isLoading={isLoadingSelectAllTransmittalRevisionsForApproval}
                >
                  {isSelectAllTransmittalRevisionsForApproval.isSelectAll ? 'Select All' : 'Unselect All'}
                </LoadingButton>,
              ];
            return headerButtons;
          },
        }}
      />
      <TransmittalItemTableFormMobileDetailModal
        cellExtras={{
          isWindowFocus,
          isRefetchingTransmittalItems,
          mutationSelectAllTransmittalRevisionsForApproval,
        }}
        docType={docType}
        hasDeletePermission={hasDeletePermission}
        queryKey={queryKey}
        isShowDetailModal={isShowDetailModal}
        useFormMethods={useFormMethods}
        transmittalId={transmittalId}
        item={items[selectedRow]}
        tableIndex={tableIndex}
        rowIndex={selectedRow}
        setSelectedRow={setSelectedRow}
        setIsBlockRefetch={setIsBlockRefetch}
        setFilesToUpload={setFilesToUpload}
        setRowUploading={setRowUploading}
        isReadOnly={isReadOnly}
      />
    </>
  );
};
